import type { WhatsAppMessage } from '../../entities/whatsAppMessage/whatsAppMessageTypes';
import { MOCK_GROUP_ID } from './mockSite';

/**
 * Mock WhatsAppMessage. Estende il tipo UI con `whatsAppSessionId` (FK mock-side
 * alla sessione), usato dagli handler MSW per costruire la struttura annidata di
 * /whatsapp-sessions — come il backend fa via il finder WhatsAppSessionId.
 *
 * I `phoneNumber` sono in forma normalizzata a cifre (come il backend) e
 * corrispondono ai telefoni dei mock Person:
 *   - 393391234567 -> Person 1 (Jader Francia, +39 339 1234567)
 *   - 393405566778 -> Person 8 (Andrea Conti, +39 340 5566778)
 *   - 393298267164 -> Person 19 (Stefano Arteconi, +39 329 8267164)
 *
 * Le date sono relative ad "adesso" così lo stato della finestra di Meta si deriva
 * come sul backend: OPEN se il contatto ha scritto entro 24h, CLOSED se le 24h
 * dall'ultimo messaggio IN sono passate, PENDING se il contatto non ha mai scritto
 * in quella sessione (abbiamo scritto solo noi).
 *
 * Attenzione: lo stato della finestra si deriva dai messaggi IN, non dall'ultimo
 * messaggio. Un nostro OUT non apre e non prolunga la finestra.
 *
 * NESSUN BINARIO. I messaggi media hanno i metadati (tipo, mimeType, nome file) ma
 * non un contenuto: in mock il player mostrerà "Media non disponibile". È voluto —
 * inventare audio finti non renderebbe più vera nessuna delle cose che i mock devono
 * far provare (distinzione nota vocale/file audio, tasto di trascrizione, blocco del
 * testo trascritto). I binari esistono solo per i file allegati durante la sessione.
 */
export interface WhatsAppMessageMock extends WhatsAppMessage {
  whatsAppSessionId: number;
}

const H = 3600 * 1000;
const D = 24 * H;
const ago = (ms: number): string => new Date(Date.now() - ms).toISOString();

const audit = {
  companyId: 1,
  groupId: MOCK_GROUP_ID,
  userId: 1,
  userName: 'Sistema',
  createDate: '2026-07-01T09:00:00Z',
  modifiedDate: '2026-07-01T09:00:00Z',
};

const BUSINESS_NUMBER_ID = '1083835191482152';

export const mockWhatsAppMessages: WhatsAppMessageMock[] = [
  // --- Sessione 71001: Person 1 (Jader Francia), ATTIVA (ultimi messaggi entro 24h) ---
  {
    whatsAppMessageId: 91001, whatsAppSessionId: 71001, direction: 'IN',
    externalId: 'wamid.mock-91001', phoneNumber: '393391234567',
    senderName: 'Jader Francia', phoneNumberId: BUSINESS_NUMBER_ID,
    body: 'Buongiorno, avete disponibilità per un sopralluogo la prossima settimana?',
    receiptDate: ago(3 * H), type: 'TEXT', ...audit,
  },
  {
    whatsAppMessageId: 91002, whatsAppSessionId: 71001, direction: 'OUT',
    externalId: 'wamid.mock-91002', phoneNumber: '393391234567',
    senderName: '', phoneNumberId: BUSINESS_NUMBER_ID,
    body: 'Certo! Le andrebbe bene martedì mattina?',
    receiptDate: ago(2 * H), type: 'TEXT', status: 'READ', statusDate: ago(2 * H),
    ...audit,
  },
  {
    whatsAppMessageId: 91003, whatsAppSessionId: 71001, direction: 'IN',
    externalId: 'wamid.mock-91003', phoneNumber: '393391234567',
    senderName: 'Jader Francia', phoneNumberId: BUSINESS_NUMBER_ID,
    body: 'Perfetto, martedì alle 10 va benissimo. Grazie!',
    receiptDate: ago(1 * H), type: 'TEXT', ...audit,
  },
  // Nota vocale GIÀ TRASCRITTA: mostra il blocco a regime (testo + riassunto +
  // cose da fare), senza tasto.
  //
  // Il mimeType è quello vero delle note vocali WhatsApp, parametri compresi: è
  // proprio il "; codecs=opus" che faceva salvare il file senza estensione.
  {
    whatsAppMessageId: 91014, whatsAppSessionId: 71001, direction: 'IN',
    externalId: 'wamid.mock-91014', phoneNumber: '393391234567',
    senderName: 'Jader Francia', phoneNumberId: BUSINESS_NUMBER_ID,
    body: '', receiptDate: ago(50 * 60 * 1000), type: 'AUDIO',
    mediaMimeType: 'audio/ogg; codecs=opus',
    mediaFileName: 'whatsapp-wamid.mock-91014.ogg',
    voiceNote: true,
    transcription:
      'Buongiorno, volevo aggiungere una cosa al sopralluogo di martedì: se possibile vorrei far vedere anche il magazzino sul retro, perché stiamo valutando di installare un secondo impianto. Se serve più tempo ditemelo pure.',
    transcriptionSummary:
      'Il contatto chiede di estendere il sopralluogo di martedì anche al magazzino sul retro, per valutare un secondo impianto.',
    transcriptionActionItems:
      'Confermare se il sopralluogo può includere il magazzino\nVerificare se serve più tempo rispetto allo slot previsto',
    transcriptionDate: ago(45 * 60 * 1000),
    ...audit,
  },
  // Nota vocale DA TRASCRIVERE: è quella su cui premere il tasto.
  {
    whatsAppMessageId: 91015, whatsAppSessionId: 71001, direction: 'IN',
    externalId: 'wamid.mock-91015', phoneNumber: '393391234567',
    senderName: 'Jader Francia', phoneNumberId: BUSINESS_NUMBER_ID,
    body: '', receiptDate: ago(40 * 60 * 1000), type: 'AUDIO',
    mediaMimeType: 'audio/ogg; codecs=opus',
    mediaFileName: 'whatsapp-wamid.mock-91015.ogg',
    voiceNote: true,
    ...audit,
  },
  // FILE AUDIO allegato, non nota vocale: stesso type AUDIO, voiceNote false. Serve
  // a vedere la differenza — player nudo, nessun tasto Trascrivi.
  {
    whatsAppMessageId: 91016, whatsAppSessionId: 71001, direction: 'IN',
    externalId: 'wamid.mock-91016', phoneNumber: '393391234567',
    senderName: 'Jader Francia', phoneNumberId: BUSINESS_NUMBER_ID,
    body: '', receiptDate: ago(35 * 60 * 1000), type: 'AUDIO',
    mediaMimeType: 'audio/mpeg', mediaFileName: 'promemoria-cantiere.mp3',
    voiceNote: false,
    ...audit,
  },

  // --- Sessione 71002: Person 1 (Jader Francia), CHIUSA (conversazione di 6 giorni fa) ---
  {
    whatsAppMessageId: 91004, whatsAppSessionId: 71002, direction: 'IN',
    externalId: 'wamid.mock-91004', phoneNumber: '393391234567',
    senderName: 'Jader Francia', phoneNumberId: BUSINESS_NUMBER_ID,
    body: 'Ho ricevuto il preventivo, grazie mille.',
    receiptDate: ago(6 * D), type: 'TEXT', ...audit,
  },
  {
    whatsAppMessageId: 91005, whatsAppSessionId: 71002, direction: 'OUT',
    externalId: 'wamid.mock-91005', phoneNumber: '393391234567',
    senderName: '', phoneNumberId: BUSINESS_NUMBER_ID,
    body: 'A disposizione per qualsiasi chiarimento. A presto!',
    receiptDate: ago(6 * D - 5 * 60 * 1000), type: 'TEXT',
    status: 'DELIVERED', statusDate: ago(6 * D - 5 * 60 * 1000), ...audit,
  },

  // --- Sessione 71003: Person 8 (Andrea Conti), ATTIVA ---
  {
    whatsAppMessageId: 91006, whatsAppSessionId: 71003, direction: 'OUT',
    externalId: 'wamid.mock-91006', phoneNumber: '393405566778',
    senderName: '', phoneNumberId: BUSINESS_NUMBER_ID,
    body: 'Buongiorno Andrea, le confermo l\'appuntamento di domani.',
    receiptDate: ago(20 * H), type: 'TEXT', status: 'READ',
    statusDate: ago(20 * H), ...audit,
  },
  {
    whatsAppMessageId: 91007, whatsAppSessionId: 71003, direction: 'IN',
    externalId: 'wamid.mock-91007', phoneNumber: '393405566778',
    senderName: 'Andrea Conti', phoneNumberId: BUSINESS_NUMBER_ID,
    body: 'Confermo anch\'io, a domani!',
    receiptDate: ago(19 * H), type: 'TEXT', ...audit,
  },

  // --- Sessione 71004: Person 19 (Stefano Arteconi), ATTIVA (ultimi messaggi entro 24h) ---
  {
    whatsAppMessageId: 91008, whatsAppSessionId: 71004, direction: 'IN',
    externalId: 'wamid.mock-91008', phoneNumber: '393298267164',
    senderName: 'Stefano Arteconi', phoneNumberId: BUSINESS_NUMBER_ID,
    body: 'Ciao, ho una domanda sul contratto.',
    receiptDate: ago(2 * H), type: 'TEXT', ...audit,
  },
  {
    whatsAppMessageId: 91009, whatsAppSessionId: 71004, direction: 'OUT',
    externalId: 'wamid.mock-91009', phoneNumber: '393298267164',
    senderName: '', phoneNumberId: BUSINESS_NUMBER_ID,
    body: 'Ciao Stefano, dimmi pure.',
    receiptDate: ago(1 * H), type: 'TEXT', status: 'DELIVERED',
    statusDate: ago(1 * H), ...audit,
  },

  // --- Sessione 71005: Person 19 (Stefano Arteconi), SCADUTA (conversazione di 7 giorni fa) ---
  {
    whatsAppMessageId: 91010, whatsAppSessionId: 71005, direction: 'OUT',
    externalId: 'wamid.mock-91010', phoneNumber: '393298267164',
    senderName: '', phoneNumberId: BUSINESS_NUMBER_ID,
    body: 'Buongiorno Stefano, le invio il documento richiesto.',
    receiptDate: ago(7 * D), type: 'TEXT', status: 'READ',
    statusDate: ago(7 * D), ...audit,
  },
  {
    whatsAppMessageId: 91011, whatsAppSessionId: 71005, direction: 'IN',
    externalId: 'wamid.mock-91011', phoneNumber: '393298267164',
    senderName: 'Stefano Arteconi', phoneNumberId: BUSINESS_NUMBER_ID,
    body: 'Ricevuto, grazie!',
    receiptDate: ago(7 * D - 5 * 60 * 1000), type: 'TEXT', ...audit,
  },

  // --- Sessione 71006: Person 19 (Stefano Arteconi), PENDING — il caso che ha
  // originato tutto. Il contatto non ha mai scritto in questa sessione: prima un
  // testo libero rifiutato da Meta (131047, finestra scaduta), poi il ricontatto
  // riuscito con un template approvato. Nessun IN => la finestra resta chiusa
  // finché non risponde lui, e il composer inline non compare.
  {
    whatsAppMessageId: 91012, whatsAppSessionId: 71006, direction: 'OUT',
    externalId: 'wamid.mock-91012', phoneNumber: '393298267164',
    senderName: '', phoneNumberId: BUSINESS_NUMBER_ID,
    body: 'Buongiorno Stefano, riprendo il discorso sul contratto.',
    receiptDate: ago(2 * D), type: 'TEXT', status: 'FAILED',
    statusDate: ago(2 * D), errorCode: 131047,
    errorMessage:
      'Message failed to send because more than 24 hours have passed since the customer last replied to this number.',
    ...audit,
  },
  {
    whatsAppMessageId: 91013, whatsAppSessionId: 71006, direction: 'OUT',
    externalId: 'wamid.mock-91013', phoneNumber: '393298267164',
    senderName: '', phoneNumberId: BUSINESS_NUMBER_ID,
    body: 'Buongiorno, la ricontattiamo dal nostro CRM.\nRiprendo il discorso sul contratto.',
    receiptDate: ago(2 * D - 5 * 60 * 1000), type: 'TEMPLATE',
    status: 'DELIVERED', statusDate: ago(2 * D - 5 * 60 * 1000), ...audit,
  },
];
